/** Authored Hero Forge reference port from fable-rogue 03a219c0
 * tools/character-builder/src/. Numerical keyframes/curves are preserved.
 * See PROVENANCE.md for the explicit skeletal adapter boundary. */
/** Small vector, quaternion and column-major matrix helpers for the character builder. */

export type V2 = [number, number];
export type V3 = [number, number, number];
export type Quat = [number, number, number, number];
/** Column-major 4×4 matrix, the layout of WGSL's mat4x4f. */
export type Mat4 = Float32Array;

export const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const scale = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
export const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export const length = (a: V3) => Math.hypot(a[0], a[1], a[2]);
export const lerp3 = (a: V3, b: V3, t: number): V3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
export function normalize(a: V3): V3 {
  const l = Math.hypot(a[0], a[1], a[2]);
  return l > 1e-12 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 1, 0];
}

export const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);
export const mix = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};

/** Deterministic value in [0, 1) for a point, so shared vertices always move and shade alike. */
export function hash3(x: number, y: number, z: number, seed = 0) {
  let h = Math.imul(Math.round(x * 4096) | 0, 0x27d4eb2d) ^ Math.imul(Math.round(y * 4096) | 0, 0x165667b1)
    ^ Math.imul(Math.round(z * 4096) | 0, 0x61c88647) ^ Math.imul(seed | 0, 0x9e3779b9);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Small seeded generator for layouts that must look the same on every visit. */
export function random(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// quaternions (x, y, z, w)
// ---------------------------------------------------------------------------

export const qIdentity = (): Quat => [0, 0, 0, 1];

export function qMul(a: Quat, b: Quat): Quat {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

export function qAxis(axis: V3, angle: number): Quat {
  const s = Math.sin(angle / 2), n = normalize(axis);
  return [n[0] * s, n[1] * s, n[2] * s, Math.cos(angle / 2)];
}

/**
 * Twist about Y first, then spread about Z, then swing about X, all about the parent's axes. For a limb hanging
 * along −Y a negative X swings it forward (+Z); for the left arm a positive Z lifts it outward (+X).
 */
export function qEuler(x: number, y = 0, z = 0): Quat {
  const qx: Quat = [Math.sin(x / 2), 0, 0, Math.cos(x / 2)];
  const qy: Quat = [0, Math.sin(y / 2), 0, Math.cos(y / 2)];
  const qz: Quat = [0, 0, Math.sin(z / 2), Math.cos(z / 2)];
  return qMul(qx, qMul(qz, qy));
}

export const qConj = (q: Quat): Quat => [-q[0], -q[1], -q[2], q[3]];

export function qNormalize(q: Quat): Quat {
  const l = Math.hypot(q[0], q[1], q[2], q[3]) || 1;
  return [q[0] / l, q[1] / l, q[2] / l, q[3] / l];
}

export function qRotate(q: Quat, v: V3): V3 {
  const [x, y, z, w] = q;
  const tx = 2 * (y * v[2] - z * v[1]), ty = 2 * (z * v[0] - x * v[2]), tz = 2 * (x * v[1] - y * v[0]);
  return [v[0] + w * tx + y * tz - z * ty, v[1] + w * ty + z * tx - x * tz, v[2] + w * tz + x * ty - y * tx];
}

export function qSlerp(a: Quat, b: Quat, t: number): Quat {
  let bx = b[0], by = b[1], bz = b[2], bw = b[3];
  let cos = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (cos < 0) { cos = -cos; bx = -bx; by = -by; bz = -bz; bw = -bw; }
  let s0 = 1 - t, s1 = t;
  if (cos < 0.9995) {
    const angle = Math.acos(cos), sin = Math.sin(angle);
    s0 = Math.sin((1 - t) * angle) / sin;
    s1 = Math.sin(t * angle) / sin;
  }
  return qNormalize([a[0] * s0 + bx * s1, a[1] * s0 + by * s1, a[2] * s0 + bz * s1, a[3] * s0 + bw * s1]);
}

/** The shortest rotation that turns direction a into direction b. */
export function qFromTo(a: V3, b: V3): Quat {
  const na = normalize(a), nb = normalize(b), d = dot(na, nb);
  if (d > 0.999999) return qIdentity();
  if (d < -0.999999) {
    const axis = Math.abs(na[0]) < 0.9 ? cross(na, [1, 0, 0]) : cross(na, [0, 1, 0]);
    return qAxis(axis, Math.PI);
  }
  const c = cross(na, nb);
  return qNormalize([c[0], c[1], c[2], 1 + d]);
}

/** The rotation whose columns are the given orthonormal, right-handed axes. */
export function qFromBasis(x: V3, y: V3, z: V3): Quat {
  const trace = x[0] + y[1] + z[2];
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    return qNormalize([(y[2] - z[1]) / s, (z[0] - x[2]) / s, (x[1] - y[0]) / s, s / 4]);
  }
  if (x[0] > y[1] && x[0] > z[2]) {
    const s = Math.sqrt(1 + x[0] - y[1] - z[2]) * 2;
    return qNormalize([s / 4, (y[0] + x[1]) / s, (z[0] + x[2]) / s, (y[2] - z[1]) / s]);
  }
  if (y[1] > z[2]) {
    const s = Math.sqrt(1 + y[1] - x[0] - z[2]) * 2;
    return qNormalize([(y[0] + x[1]) / s, s / 4, (z[1] + y[2]) / s, (z[0] - x[2]) / s]);
  }
  const s = Math.sqrt(1 + z[2] - x[0] - y[1]) * 2;
  return qNormalize([(z[0] + x[2]) / s, (z[1] + y[2]) / s, s / 4, (x[1] - y[0]) / s]);
}

// ---------------------------------------------------------------------------
// matrices
// ---------------------------------------------------------------------------

export function m4Identity(out: Mat4 = new Float32Array(16)): Mat4 {
  out.fill(0);
  out[0] = out[5] = out[10] = out[15] = 1;
  return out;
}

/** Translation · rotation · scale. */
export function m4Compose(q: Quat, t: V3, s: V3 = [1, 1, 1], out: Mat4 = new Float32Array(16)): Mat4 {
  const [x, y, z, w] = q;
  const x2 = x + x, y2 = y + y, z2 = z + z;
  const xx = x * x2, xy = x * y2, xz = x * z2, yy = y * y2, yz = y * z2, zz = z * z2, wx = w * x2, wy = w * y2, wz = w * z2;
  out[0] = (1 - (yy + zz)) * s[0]; out[1] = (xy + wz) * s[0]; out[2] = (xz - wy) * s[0]; out[3] = 0;
  out[4] = (xy - wz) * s[1]; out[5] = (1 - (xx + zz)) * s[1]; out[6] = (yz + wx) * s[1]; out[7] = 0;
  out[8] = (xz + wy) * s[2]; out[9] = (yz - wx) * s[2]; out[10] = (1 - (xx + yy)) * s[2]; out[11] = 0;
  out[12] = t[0]; out[13] = t[1]; out[14] = t[2]; out[15] = 1;
  return out;
}

/** The product written where `out` is asked for (at `offset` in it); only an `out` that is also `a` or `b` goes through a scratch. */
const product = new Float32Array(16);
export function m4Mul(a: ArrayLike<number>, b: ArrayLike<number>, out: Mat4 = new Float32Array(16), offset = 0): Mat4 {
  const r = out === a || out === b ? product : out, at = r === out ? offset : 0;
  for (let c = 0; c < 4; c++) {
    for (let row = 0; row < 4; row++) {
      r[at + c * 4 + row] = a[row] * b[c * 4] + a[4 + row] * b[c * 4 + 1] + a[8 + row] * b[c * 4 + 2] + a[12 + row] * b[c * 4 + 3];
    }
  }
  if (r !== out) out.set(r, offset);
  return out;
}

export function m4Invert(m: ArrayLike<number>, out: Mat4 = new Float32Array(16)): Mat4 {
  const [a00, a01, a02, a03, a10, a11, a12, a13, a20, a21, a22, a23, a30, a31, a32, a33] = Array.from(m);
  const b00 = a00 * a11 - a01 * a10, b01 = a00 * a12 - a02 * a10, b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11, b04 = a01 * a13 - a03 * a11, b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30, b07 = a20 * a32 - a22 * a30, b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31, b10 = a21 * a33 - a23 * a31, b11 = a22 * a33 - a23 * a32;
  const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  const inv = det ? 1 / det : 0;
  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * inv;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * inv;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * inv;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * inv;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * inv;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * inv;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * inv;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * inv;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * inv;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * inv;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * inv;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * inv;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * inv;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * inv;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * inv;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * inv;
  return out;
}

export function m4Point(m: ArrayLike<number>, p: V3): V3 {
  return [
    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14],
  ];
}

/** Right-handed perspective with WebGPU's 0..1 depth range. */
export function m4Perspective(fovY: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovY / 2), out = new Float32Array(16);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = far / (near - far);
  out[11] = -1;
  out[14] = (near * far) / (near - far);
  return out;
}

/** Right-handed orthographic projection with WebGPU's 0..1 depth range. */
export function m4Ortho(left: number, right: number, bottom: number, top: number, near: number, far: number): Mat4 {
  const out = new Float32Array(16);
  out[0] = 2 / (right - left);
  out[5] = 2 / (top - bottom);
  out[10] = 1 / (near - far);
  out[12] = -(right + left) / (right - left);
  out[13] = -(top + bottom) / (top - bottom);
  out[14] = near / (near - far);
  out[15] = 1;
  return out;
}

export function m4LookAt(eye: V3, target: V3, up: V3 = [0, 1, 0]): Mat4 {
  const z = normalize(sub(eye, target)), x = normalize(cross(up, z)), y = cross(z, x), out = new Float32Array(16);
  out[0] = x[0]; out[4] = x[1]; out[8] = x[2];
  out[1] = y[0]; out[5] = y[1]; out[9] = y[2];
  out[2] = z[0]; out[6] = z[1]; out[10] = z[2];
  out[12] = -dot(x, eye); out[13] = -dot(y, eye); out[14] = -dot(z, eye); out[15] = 1;
  return out;
}

/** sRGB hex such as '#b76f62' to 0..1 channels (still sRGB-encoded). */
export function hex(color: string): V3 {
  const n = parseInt(color.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Scale an sRGB colour's brightness (in linear light) and return sRGB again. */
export function shade(color: V3, factor: number): V3 {
  return color.map(c => {
    const linear = Math.pow(c, 2.2) * factor;
    return Math.min(1, Math.pow(Math.max(0, linear), 1 / 2.2));
  }) as V3;
}
